import { useState } from 'react'
import { Link, router, useForm } from '@inertiajs/react'
import AppLayout from '@/layouts/AppLayout'
import { PageContainer } from '@/components/PageContainer'
import { PageHeader } from '@/components/PageHeader'
import { FlashBanner } from '@/components/FlashBanner'
import { FormField } from '@/components/FormField'
import { FieldError } from '@/components/FieldError'
import { ConfirmDialog } from '@/components/ConfirmDialog'
import { ErrorBanner } from '@/components/ErrorBanner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import type { ProjectDetail } from '@/types/project'
import type { ResultError } from '@/types/form'
import { Industry } from '@/types/industry.ts'

interface Props {
  project_detail: ProjectDetail
  error?: ResultError
  industries: Industry[]
}

export default function ProjectsEdit ({ project_detail: project, error, industries }: Props): React.ReactElement {
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const { data, setData, patch, processing, errors } = useForm({
    name: project.name,
    description: project.description ?? '',
    industry_id: project.industry?.id ?? ''
  })

  function handleSubmit (e: React.SubmitEvent): void {
    e.preventDefault()
    patch(`/projects/${project.id}`)
  }

  function handleDelete (): void {
    setDeleting(true)
    router.delete(`/projects/${project.id}`, {
      onFinish: () => setDeleting(false)
    })
  }

  return (
    <AppLayout project={project}>
      <PageContainer maxWidth='640px'>
        <PageHeader
          title='プロジェクト設定'
          backHref={`/projects/${project.id}`}
          backLabel={project.name}
        />

        <FlashBanner />

        {(error != null) && (errors.name == null || errors.name === '') && <ErrorBanner message={error.error_message} />}

        <form onSubmit={handleSubmit}>
          <div className='bg-(--bp-surface) border border-(--bp-border) rounded-[10px]'>
            <div className='p-6'>
              <FormField label='プロジェクト名' required>
                <Input
                  type='text'
                  value={data.name}
                  onChange={(e) => setData('name', e.target.value)}
                />
                <FieldError message={errors.name ?? error?.errors?.name?.[0]} />
              </FormField>
              <FormField label='業種（任意）'>
                <select
                  className='w-full py-[9px] px-3 border border-(--bp-border) rounded-[7px] text-sm text-(--bp-ink) bg-(--bp-surface) outline-none transition-all focus:border-(--bp-accent) focus:shadow-[0_0_0_3px_var(--bp-accent-pale)]'
                  value={data.industry_id}
                  onChange={(e) => setData('industry_id', e.target.value)}
                >
                  <option value=''>選択してください</option>
                  {industries.map((industry, index) => (
                    <option key={industry.id} value={industry.id}>{industry.label}</option>
                  ))}
                </select>
              </FormField>
              <FormField label='説明（任意）'>
                <textarea
                  className='w-full py-[9px] px-3 border border-(--bp-border) rounded-[7px] text-sm text-(--bp-ink) bg-(--bp-surface) outline-none transition-all focus:border-(--bp-accent) focus:shadow-[0_0_0_3px_var(--bp-accent-pale)]'
                  style={{ minHeight: 90, resize: 'vertical' }}
                  value={data.description}
                  onChange={(e) => setData('description', e.target.value)}
                />
              </FormField>
            </div>
            <div className='px-6 py-4 bg-(--bp-surface2) border-t border-(--bp-border) rounded-b-[10px] flex gap-2 justify-end'>
              <Button asChild variant='outline'>
                <Link href={`/projects/${project.id}`}>
                  キャンセル
                </Link>
              </Button>
              <Button type='submit' disabled={processing}>
                変更を保存する
              </Button>
            </div>
          </div>
        </form>

        <div className='mt-8 py-5 px-6 border border-(--bp-critical-border) rounded-[10px] bg-(--bp-critical-pale)'>
          <div className='text-[13px] font-bold text-(--bp-critical) mb-1'>危険な操作</div>
          <div className='text-xs text-(--bp-critical) opacity-80 mb-[14px] leading-[1.5]'>
            プロジェクトを削除すると、すべての要件定義データ・ヒアリングノート・品質チェック結果が完全に削除されます。この操作は取り消せません。
          </div>
          <Button variant='destructive' type='button' onClick={() => setConfirmOpen(true)}>
            プロジェクトを削除する
          </Button>
        </div>

        <ConfirmDialog
          open={confirmOpen}
          onClose={() => setConfirmOpen(false)}
          onConfirm={handleDelete}
          title='プロジェクトを削除しますか？'
          description='すべてのデータが完全に削除されます。この操作は取り消せません。'
          processing={deleting}
        />
      </PageContainer>
    </AppLayout>
  )
}
